import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import { notes as notesApi } from "./api";
import { listDrafts, getDraft, removeDraft } from "./note-drafts";
import NoteEditor from "../components/NoteEditor";

const NotesContext = createContext(null);
export const useNotes = () => useContext(NotesContext);

function sortNotes(list) {
    return [...list].sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });
}

export function NotesProvider({ children }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadedRef = useRef(false);
    // editor: { open, note (nota completa | null = nova), loading, key }
    const [editor, setEditor] = useState({ open: false, note: null, loading: false, key: 0 });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await notesApi.list();
            setNotes(sortNotes(Array.isArray(data) ? data : []));
            loadedRef.current = true;
        } catch {
            toast.error("Não foi possível carregar suas notas.");
        } finally {
            setLoading(false);
        }
    }, []);

    const ensureLoaded = useCallback(() => {
        if (!loadedRef.current) refresh();
    }, [refresh]);

    const openNewNote = useCallback(() => {
        setEditor((e) => ({ open: true, note: null, loading: false, key: e.key + 1 }));
    }, []);

    const openNote = useCallback(async (meta) => {
        setEditor((e) => ({ open: true, note: null, loading: true, key: e.key + 1 }));
        try {
            let full = await notesApi.get(meta.id);
            // Se existe rascunho local MAIS NOVO que a versão do servidor
            // (ex.: navegador fechou sem internet), restaura o rascunho.
            const draft = getDraft(meta.id);
            if (draft && draft.savedAt && new Date(draft.savedAt) > new Date(full.updated_at)) {
                full = {
                    ...full,
                    title: draft.title ?? full.title,
                    content: draft.content ?? full.content,
                    pinned: draft.pinned ?? full.pinned,
                    color: draft.color ?? full.color,
                    _fromDraft: true,
                };
            }
            setEditor((e) => ({ ...e, note: full, loading: false }));
        } catch {
            // Servidor/internet fora? Abre com o rascunho local, se houver.
            const draft = getDraft(meta.id);
            if (draft) {
                setEditor((e) => ({
                    ...e,
                    note: {
                        id: meta.id,
                        title: draft.title || meta.title || "",
                        content: draft.content || "",
                        pinned: !!(draft.pinned ?? meta.pinned),
                        color: draft.color ?? meta.color ?? "",
                        updated_at: meta.updated_at,
                        _fromDraft: true,
                    },
                    loading: false,
                }));
                toast.info("Sem conexão — abrindo a versão salva no dispositivo.");
                return;
            }
            toast.error("Não foi possível abrir a nota.");
            setEditor((e) => ({ ...e, open: false, loading: false }));
        }
    }, []);

    const closeEditor = useCallback(() => {
        setEditor((e) => ({ ...e, open: false, note: null, loading: false }));
    }, []);

    const upsertInList = useCallback((note) => {
        setNotes((prev) => {
            const meta = { ...note };
            delete meta.content; // lista guarda só metadados + preview
            const idx = prev.findIndex((n) => n.id === meta.id);
            const next = idx >= 0 ? prev.map((n, i) => (i === idx ? { ...n, ...meta } : n)) : [meta, ...prev];
            return sortNotes(next);
        });
    }, []);

    const createNote = useCallback(async (payload) => {
        const note = await notesApi.create(payload);
        upsertInList(note);
        return note;
    }, [upsertInList]);

    const updateNote = useCallback(async (id, payload) => {
        const note = await notesApi.update(id, payload);
        upsertInList(note);
        return note;
    }, [upsertInList]);

    const removeNote = useCallback(async (id) => {
        await notesApi.remove(id);
        removeDraft(id); // descarta rascunho local — exclusão foi intencional
        setNotes((prev) => prev.filter((n) => n.id !== id));
        setEditor((e) => (e.open && e.note?.id === id ? { ...e, open: false, note: null } : e));
    }, []);

    const togglePin = useCallback(async (note) => {
        try {
            await updateNote(note.id, { pinned: !note.pinned });
        } catch {
            toast.error("Não foi possível fixar a nota.");
        }
    }, [updateNote]);

    // ------------------------------------------------------------------
    // Sincronização de rascunhos pendentes (autosave à prova de falhas):
    // roda ao abrir o app, quando a internet volta e a cada 30s enquanto
    // houver rascunhos não enviados. O rascunho aberto no editor fica de
    // fora (o próprio editor cuida dele).
    // ------------------------------------------------------------------
    const openDraftIdRef = useRef(null);
    const setOpenDraftId = useCallback((id) => { openDraftIdRef.current = id; }, []);
    const syncingRef = useRef(false);

    const syncPendingDrafts = useCallback(async () => {
        if (syncingRef.current) return;
        const drafts = listDrafts();
        if (drafts.length === 0) return;
        syncingRef.current = true;
        let synced = 0;
        try {
            for (const d of drafts) {
                if (openDraftIdRef.current && d.draftId === openDraftIdRef.current) continue;
                const payload = {
                    title: d.title || "",
                    content: d.content || "",
                    pinned: !!d.pinned,
                    color: d.color || "",
                };
                if (!payload.title.trim() && !payload.content.trim()) {
                    removeDraft(d.draftId);
                    continue;
                }
                try {
                    if (d.noteId) await updateNote(d.noteId, payload);
                    else await createNote(payload);
                    removeDraft(d.draftId);
                    synced++;
                } catch (err) {
                    // Nota excluída no servidor: recria para não perder o texto.
                    if (err?.response?.status === 404) {
                        try {
                            await createNote(payload);
                            removeDraft(d.draftId);
                            synced++;
                        } catch { /* tenta na próxima rodada */ }
                    }
                    // Outros erros (offline/5xx): mantém o rascunho e tenta depois.
                }
            }
        } finally {
            syncingRef.current = false;
        }
        if (synced > 0) toast.success(synced === 1 ? "1 nota pendente foi sincronizada." : `${synced} notas pendentes foram sincronizadas.`);
    }, [createNote, updateNote]);

    useEffect(() => {
        syncPendingDrafts();
        const onOnline = () => syncPendingDrafts();
        window.addEventListener("online", onOnline);
        const iv = setInterval(() => {
            if (listDrafts().length > 0) syncPendingDrafts();
        }, 30000);
        return () => {
            window.removeEventListener("online", onOnline);
            clearInterval(iv);
        };
    }, [syncPendingDrafts]);

    const value = {
        notes, loading, refresh, ensureLoaded,
        editor, openNote, openNewNote, closeEditor,
        createNote, updateNote, removeNote, togglePin,
        setOpenDraftId, syncPendingDrafts,
    };

    return (
        <NotesContext.Provider value={value}>
            {children}
            <NoteEditor />
        </NotesContext.Provider>
    );
}
